import {
  Component,
  forwardRef,
  input,
  computed,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';

import { VOneIconComponent } from '@shared-components';
import { IconType } from '@shared-enums';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'text';
export type IconPosition = 'left' | 'right';

@Component({
  selector: 'v-one-button',
  imports: [forwardRef(() => VOneIconComponent)],
  templateUrl: './v-one-button.component.html',
  styleUrls: ['./v-one-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneButtonComponent {
  // Inputs
  text = input<string>('');
  variant = input<ButtonVariant>('primary');
  disabled = input<boolean>(false);
  icon = input<string>('');
  iconPosition = input<IconPosition>('left');
  iconType = input<IconType>(IconType.OUTLINE);
  type = input<'button' | 'submit' | 'reset'>('button');
  className = input<string>('');

  // Outputs
  buttonClick = output<Event>();
  buttonFocus = output<FocusEvent>();
  buttonBlur = output<FocusEvent>();
  mouseEnter = output<MouseEvent>();
  mouseLeave = output<MouseEvent>();
  mouseDown = output<MouseEvent>();
  mouseUp = output<MouseEvent>();
  keyDown = output<KeyboardEvent>();
  keyUp = output<KeyboardEvent>();

  // Computed properties
  readonly buttonClasses = computed(() => {
    const baseClass = 'v-one-button';
    const variantClass = `${baseClass}--${this.variant()}`;
    const disabledClass = this.disabled() ? `${baseClass}--disabled` : '';
    const iconOnlyClass =
      !this.text() && this.icon() ? `${baseClass}--icon-only` : '';
    const customClass = this.className();

    return [baseClass, variantClass, disabledClass, iconOnlyClass, customClass]
      .filter(Boolean)
      .join(' ');
  });

  readonly showLeftIcon = computed(() => {
    return this.icon() && this.iconPosition() === 'left';
  });

  readonly showRightIcon = computed(() => {
    return this.icon() && this.iconPosition() === 'right';
  });

  handleClick(event: Event): void {
    if (!this.disabled()) {
      this.buttonClick.emit(event);
    }
  }

  handleFocus(event: FocusEvent): void {
    if (!this.disabled()) {
      this.buttonFocus.emit(event);
    }
  }

  handleBlur(event: FocusEvent): void {
    if (!this.disabled()) {
      this.buttonBlur.emit(event);
    }
  }

  handleMouseEnter(event: MouseEvent): void {
    if (!this.disabled()) {
      this.mouseEnter.emit(event);
    }
  }

  handleMouseLeave(event: MouseEvent): void {
    if (!this.disabled()) {
      this.mouseLeave.emit(event);
    }
  }

  handleMouseDown(event: MouseEvent): void {
    if (!this.disabled()) {
      this.mouseDown.emit(event);
    }
  }

  handleMouseUp(event: MouseEvent): void {
    if (!this.disabled()) {
      this.mouseUp.emit(event);
    }
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (!this.disabled()) {
      this.keyDown.emit(event);

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.handleClick(event);
      }
    }
  }

  handleKeyUp(event: KeyboardEvent): void {
    if (!this.disabled()) {
      this.keyUp.emit(event);
    }
  }
}
